import {
  forwardRef,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { useMergeRefs } from "../../../hooks";
import { safeHidePopover, safeShowPopover } from "../../../internal/functions";

import { useToasts } from "./useToasts";

import styles from "./Toaster.module.scss";

/**
 * Props for the ToasterPopover component
 * @extends PropsWithChildren
 */
type ToasterPopoverProps = PropsWithChildren;

/**
 * ToasterPopover component for displaying toast notifications in a popover.
 *
 * Features:
 * - Displays toast notifications in a native popover element
 * - Automatically shows/hides based on toast count
 * - Handles dialog toaster state management
 * - Browser compatibility checking for popover support
 * - Smooth transitions between show/hide states
 * - Integration with toast provider context
 *
 * @example
 * <ToasterPopover>
 *   <ToastStack toasts={toastList} />
 * </ToasterPopover>
 */
export const ToasterPopover = forwardRef<HTMLDivElement, ToasterPopoverProps>(
  (props, ref) => {
    const { isDialogToaster } = useToasts();
    const popoverRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs([popoverRef, ref]);

    const { toasts } = useToasts();
    const toastCount = useMemo(() => toasts.length, [toasts]);

    useEffect(() => {
      if (!popoverRef.current) return;
      const currentPopover = popoverRef.current;
      let timeout: NodeJS.Timeout | undefined;

      if (toastCount > 0) {
        if (isDialogToaster) {
          // For dialog toasters, we need to reset the popover state
          timeout = setTimeout(() => {
            safeHidePopover(currentPopover);
            safeShowPopover(currentPopover);
          });
        } else {
          // For non-dialog toasters, just show the popover
          safeShowPopover(currentPopover);
        }
      } else {
        safeHidePopover(currentPopover);
      }

      return () => {
        if (timeout) clearTimeout(timeout);
        if (toastCount === 0) {
          safeHidePopover(currentPopover);
        }
      };
    }, [toastCount, isDialogToaster]);

    return (
      <div ref={combinedRef} className={styles.popover} popover="manual">
        {props.children}
      </div>
    );
  },
);

ToasterPopover.displayName = "ToasterPopover";
